import React, { useRef, useState } from 'react';
import styles from './COProductCard.module.scss';
import cardStyles from './card.module.scss';
import { ReactComponent as CopyIcon } from 'assets/icons/duplicate.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { COProductCardPropsEnums, MenuItemKey } from 'enums/COProductCardEnums';
import { Dropdown, Menu, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import CarbonLabel from 'assets/images/carbonLabel.png';
import { toPng } from 'html-to-image';
import ProductImage from 'assets/images/product_img1.png';
import COButton from '../COButton';
import { ReactComponent as ApprovalReportIcon } from 'assets/icons/app-report.svg';
import formatNumberWithCommas from 'shared/utils/formatNumberWithComma';
import { COProductCardProps, MenuItem, renderIcon } from './constants';
import { ProductDashboardCardViewProps } from 'enums/ProductDashboardCardEnums';
import ProcessingFacilityModal from '../Products/ProcessingFacilityModal';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import ValueChainService from 'services/ValueChainService/valuechain.service';
import { useEffect } from 'react';
import { AllProductListTableFields } from 'enums/AllProductListEnums';
import { ChildProduct } from 'models/ChildProduct/childProduct.model';
import { Commodity } from 'models/CommodityList/Commodity.model';
import { useYearLabel } from 'shared/utils/generateYearSpan';
import FarmboxCommodityDistributionService from 'services/Farmbox/CommodityDistribution/commodityDistribution.service';
import AgricultureIcon from 'assets/icons/agriculturetype.svg';
import LivestockIcon from 'assets/icons/livestocktypeicon.svg';
import DashboardService from 'services/DashboardService/dashboard.service';
import CardDetails from './CardDetails';
import { CommodityTypes } from 'constants/FarmboxConstants/stage0.constants';
import FarmIcon from 'assets/icons/farm.svg';
import { FormCommodityTypes } from 'enums/FarmPageEnum';

const COProductCard: React.FC<COProductCardProps> = ({
  data,
  multiRowCard,
  commodityCard,
  commodityDetailsCard,
  showDownloadOptions,
  iconRender,
  showStatus,
  declineOption,
  calenderRow,
  onDeleteClick,
  onCopyClick,
  navigateProductOverview,
  onDeclineReasonClick,
  isProduct,
}: COProductCardProps) => {
  const { viewApprovalReport } = DashboardService();
  const { downloadApprovalReport } = FarmboxCommodityDistributionService();
  const { fetchProcessingFacilities, processingFacilityList } =
    ValueChainService();
  const { organizationData } = useParentOrganizationContext();
  const [createProcessingFacility, setCreateProcessingFacility] =
    useState<boolean>(false);
  useEffect(() => {
    if (!commodityCard) {
      fetchProcessingFacilities();
    }
  }, []);

  const handleNavigation = () => {
    navigateProductOverview(
      data.commodityId,
      data.stage,
      data?.status,
      data?.commodityType
    );
  };
  const isChildProduct = (
    data: ChildProduct | Commodity
  ): data is ChildProduct => {
    return (data as ChildProduct).b2c !== undefined;
  };

  const getImageSource = () => {
    if (isChildProduct(data)) {
      return data.image ?? ProductImage;
    }
    const desiredImage = commodityCard ? FarmIcon : ProductImage;
    return data.imageUrl ?? desiredImage;
  };

  const getCarbonValue = () => {
    if (isChildProduct(data)) {
      return data.b2c?.toFixed(4);
    }
    return data.emission?.toFixed(4);
  };

  const getLocationName = isChildProduct(data)
    ? data.processingFacilityName
    : data.farm;

  const yearLabel = useYearLabel(String((data as Commodity)?.harvestYear));

  const getReportingPeriod = isChildProduct(data)
    ? data.reportingPeriod
    : yearLabel;

  const carbonValue = getCarbonValue();
  const isLargeCrarbonValue = carbonValue && carbonValue.toString().length > 12;
  const carbonLabelRef = useRef<HTMLDivElement>(null);
  const htmlToImageConvert = () => {
    if (carbonLabelRef.current) {
      toPng(carbonLabelRef.current, { cacheBust: true })
        .then(dataUrl => {
          const link = document.createElement('a');
          link.download = `${data?.name}-carbon-footprint.png`;
          link.href = dataUrl;
          link.click();
        })
        .catch(err => {
          console.log('Error occur while conversion ', err);
        });
    }
  };
  const viewReport = (id: number) =>
    commodityCard
      ? downloadApprovalReport(id, data.commodityType === 'livestock')
      : viewApprovalReport(id);
  const handleOpenCreateProductModal = (id: number) => {
    if (
      processingFacilityList?.length &&
      organizationData?.organization?.name
    ) {
      onCopyClick?.(id);
    } else {
      setCreateProcessingFacility(true);
    }
  };

  const handleMenuItemClick = (item: MenuItem, id: number, type?: string) => {
    item?.domEvent?.stopPropagation();
    switch (item.key) {
      case MenuItemKey.REPORT:
        viewReport(id);
        break;
      case MenuItemKey.LABEL:
        htmlToImageConvert();
        break;
      case MenuItemKey.COPY:
        handleOpenCreateProductModal(id);
        break;
      case MenuItemKey.DELETE:
        onDeleteClick?.(id, type);
        break;
      default:
    }
  };
  const handleProcessingFacilityModalClose = () =>
    setCreateProcessingFacility(false);
  let desiredSrc = '';
  let desiredImageAlt = '';
  if (data?.commodityType === CommodityTypes.LIVE_STOCK) {
    desiredSrc = LivestockIcon;
    desiredImageAlt = FormCommodityTypes.LIVE_STOCK;
  } else {
    desiredSrc = AgricultureIcon;
    desiredImageAlt = FormCommodityTypes.AGRICULTURE;
  }

  const menu = (
    <Menu
      onClick={item =>
        handleMenuItemClick(item, Number(data.commodityId), data?.commodityType)
      }>
      {showDownloadOptions === AllProductListTableFields.APPROVED && (
        <>
          <Menu.Item key={MenuItemKey.REPORT} icon={<ApprovalReportIcon />}>
            {COProductCardPropsEnums.APPROVAL_REPORT}
          </Menu.Item>

          {!commodityCard && (
            <Menu.Item
              key={MenuItemKey.LABEL}
              icon={
                <div className={styles.downloadIconContainer}>
                  <DownloadIcon className={styles.labelIcon} />
                </div>
              }>
              {COProductCardPropsEnums.LABEL}
            </Menu.Item>
          )}
        </>
      )}
      {!commodityCard && (
        <Menu.Item key={MenuItemKey.COPY} icon={<CopyIcon />}>
          {COProductCardPropsEnums.DUPLICATE}
        </Menu.Item>
      )}
      <Menu.Item
        key={MenuItemKey.DELETE}
        icon={<DeleteIcon className={styles.deleteIcon} />}>
        <div className={styles.redText}>{COProductCardPropsEnums.DELETE}</div>
      </Menu.Item>
    </Menu>
  );
  const { KG_CARBON_EMISSION, CONSUMER_UNIT_TWO } =
    ProductDashboardCardViewProps;
  const stopPropagationHandler = (event: React.MouseEvent<HTMLElement>) =>
    event.stopPropagation();
  return (
    <div
      className={clsx(styles.productCard, cardStyles.productCard)}
      onClick={handleNavigation}>
      <div className={cardStyles.rowOne}>
        <div className={cardStyles.productContainer}>
          <div className={cardStyles.productImageContainer}>
            <img
              src={getImageSource()}
              alt={data.name}
              className={cardStyles.productImage}
            />
          </div>
          <div className={cardStyles.productLabel}>
            <Tooltip title={data.name}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                className={cardStyles.productName}>
                {data.name}
              </Typography.Paragraph>
            </Tooltip>
          </div>
          {!isProduct && (
            <div className={cardStyles.productTypeContainer}>
              <img src={desiredSrc} alt={desiredImageAlt} />
            </div>
          )}
        </div>
        <div className={cardStyles.iconContainer}>
          <div className={cardStyles.mainIcon}>
            {iconRender && renderIcon(cardStyles, data.status)}
          </div>
          <div className={styles.copyIconContainer}>
            <Dropdown overlay={menu} trigger={['click']}>
              <COButton text onClick={stopPropagationHandler}>
                <div className={styles.dotsText}>...</div>
              </COButton>
            </Dropdown>
          </div>
        </div>
      </div>
      <CardDetails
        calenderRow={calenderRow}
        commodityDetailsCard={commodityDetailsCard}
        commodityCard={commodityCard}
        multiRowCard={multiRowCard}
        showStatus={showStatus}
        declineOption={declineOption}
        data={data}
        getReportingPeriod={getReportingPeriod}
        getLocationName={getLocationName}
        getCarbonValue={getCarbonValue}
        onDeclineReasonClick={onDeclineReasonClick}
      />

      <div
        className={clsx({
          [styles.carbonLabelDownload]: true,
        })}>
        <div
          ref={carbonLabelRef}
          className={clsx({
            [styles.footprintImage]: true,
            [styles.large]: isLargeCrarbonValue,
          })}>
          <img src={CarbonLabel} />
          <div>
            <div>{formatNumberWithCommas(carbonValue)}</div>
            <div>{KG_CARBON_EMISSION}</div>
            <div>{CONSUMER_UNIT_TWO}</div>
          </div>
        </div>
      </div>
      <ProcessingFacilityModal
        modalOpen={createProcessingFacility}
        handleClose={handleProcessingFacilityModalClose}
      />
    </div>
  );
};
export default COProductCard;
